import { Dialog, useDialogRoute } from "@dash/dialog";
import { DateRangeFilter, SingleSelectSimpleFilter } from "@dash/filters";
import { Button, TextField } from "@dash/form";
import { Page, PageHeader } from "@dash/page";
import { Pagination } from "@dash/search";
import { QuerySkeleton } from "@dash/skeleton";
import { useTheme } from "@emotion/react";
import { useState } from "react";

import { notificationPermissionMap } from "@megaron/notifications-contracts";
import { useClientManager } from "@megaron/react-clients";

import { AllNotificationsTable } from "./AllNotificationsTable";
import { useNotificationsFilters } from "./useNotificationsFilters";

export const NotificationsPage = () => {
  const theme = useTheme();
  const pageSize = 20;
  const { filters, setFilter, setFilters } = useNotificationsFilters();

  const notificationsLogsQuery = useClientManager("notifications")
    .getNotificationLogs()
    .useQuery({
      offset: (filters.page ?? 0) * pageSize,
      limit: pageSize,
      startDate: filters.startDate ? new Date(filters.startDate) : undefined,
      endDate: filters.endDate ? new Date(filters.endDate) : undefined,
      app: filters.app ? filters.app : undefined,
    });

  const testNotificationMutation = useClientManager("notifications").produceTestNotificationToOtherUser().useMutation();

  const uniqueApps = Array.from(new Set(Object.values(notificationPermissionMap).map((p) => p.app))).map((app) => ({
    label: app,
    value: app,
  }));

  const [testUserId, setTestUserId] = useState("");

  const testNotificationDialog = useDialogRoute("/test-notification", ({ onClose }) => (
    <Dialog onClose={onClose} header="Wyślij testowy webpush" css={{ width: "400px" }}>
      <div css={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        <TextField label="ID użytkownika" value={testUserId} onChange={setTestUserId} />
        <Button
          onClick={() => {
            if (!testUserId) return;
            testNotificationMutation.mutate({ recipientId: testUserId });
            onClose();
          }}
        >
          Wyślij powiadomienie
        </Button>
      </div>
    </Dialog>
  ));

  return (
    <Page css={{ marginTop: "1rem" }}>
      <PageHeader>Powiadomienia</PageHeader>
      <div
        css={{
          display: "flex",
          gap: "1rem",
          marginBottom: "1rem",
          flexWrap: "wrap",
          width: "100%",
        }}
      >
        <DateRangeFilter
          variant="secondary"
          startDate={filters.startDate ? new Date(filters.startDate) : undefined}
          endDate={filters.endDate ? new Date(filters.endDate) : undefined}
          withTime
          onChange={(range) => {
            setFilters({
              ...filters,
              startDate: range.startDate,
              endDate: range.endDate,
              page: 0,
            });
          }}
        />

        <SingleSelectSimpleFilter
          css={{
            height: "37px",
          }}
          label={"Aplikacja"}
          value={filters.app}
          options={uniqueApps}
          onChange={setFilter("app")}
        />

        <Button
          variant="outline"
          css={{ height: "37px", alignSelf: "flex-end", background: "white", border: "none" }}
          onClick={testNotificationDialog.open}
        >
          Wyślij testowy webpush
        </Button>

        <Pagination
          css={{ maxHeight: "41px", alignSelf: "flex-end" }}
          page={filters.page || 0}
          onPageChange={(page) => {
            setFilter("page")(page);
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          pageSize={pageSize}
          itemCount={notificationsLogsQuery.data?.count}
        />
      </div>

      <QuerySkeleton query={notificationsLogsQuery}>
        {(result) => (
          <AllNotificationsTable notifications={result.items} isLoading={notificationsLogsQuery.isLoading} />
        )}
      </QuerySkeleton>

      {testNotificationDialog.element}
    </Page>
  );
};
